/**
 * Track Root — שורש האזור
 *
 * Owns everything under one track's base path (/pensia, /mashkanta) and decides
 * which chrome to render:
 *
 *   anonymous  → TrackEntryPage, full screen, no sidebar (it is a public page)
 *   signed in  → the app Layout with the hub / topic / chat inside
 *
 * Keeping ONE url for both states is deliberate: a marketing URL that differs
 * from the app URL is what breaks every link people share.
 *
 * The onboarding chain (agreement → questionnaire → risk approval) is opted OUT
 * of here via requireClientOnboarding={false}: these areas are free and unsigned,
 * so dragging a /pensia visitor to an advisory agreement would be wrong. That
 * gate still applies everywhere else, because the prop defaults to true.
 */

import { Routes, Route, Navigate } from 'react-router-dom'
import Layout from '../../components/layout/Layout'
import PublicLayout from '../../components/layout/PublicLayout'
import ProtectedRoute from '../../components/auth/ProtectedRoute'
import TrackEntryPage from './TrackEntryPage'
import TrackHubPage from './TrackHubPage'
import TrackTopicPage from './TrackTopicPage'
import TrackChatPage from './TrackChatPage'
import TrackSignupPage from './TrackSignupPage'
import { useAuthStore } from '../../store/authStore'
import type { TrackKey } from '../../config/tracks'

interface TrackRootProps {
  track: TrackKey
}

export default function TrackRoot({ track }: TrackRootProps) {
  const { isAuthenticated } = useAuthStore()

  return (
    <Routes>
      {/* Public: signup always, and the entry page when signed out. */}
      <Route element={<PublicLayout />}>
        <Route path="signup" element={<TrackSignupPage track={track} />} />
        {!isAuthenticated && <Route path="" element={<TrackEntryPage track={track} />} />}
      </Route>

      {/* Signed in: the app chrome. */}
      <Route element={<Layout />}>
        <Route
          path=""
          element={
            <ProtectedRoute requireClientOnboarding={false}>
              <TrackHubPage track={track} />
            </ProtectedRoute>
          }
        />
        <Route
          path="topic/:slug"
          element={
            <ProtectedRoute requireClientOnboarding={false}>
              <TrackTopicPage track={track} />
            </ProtectedRoute>
          }
        />
        <Route
          path="chat"
          element={
            <ProtectedRoute requireClientOnboarding={false}>
              <TrackChatPage track={track} />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Unknown sub-path inside the track → the track root, not the app root. */}
      <Route path="*" element={<Navigate to="" replace />} />
    </Routes>
  )
}
